import { useTranslation } from 'react-i18next';
import { useState, useEffect, FC } from 'react';
import {
  TeButton,
  TeContainer,
  TeContainerSlim,
  TeIcon,
  TeImage,
  TeModal,
} from '@topcon/tuicl-react';
import { styled } from 'styled-components';
import { sizeToNumber } from '@topcon/tuicl/dist/common/utils';

import { getGravatarUrl } from '../utils/utils';
import UploadFileArea, { IUploadFile } from './common/FileUpload/UploadFileArea';
import AdjustLogoModal from '../pages/Settings/AdjustLogoModal';

interface Logo {
  url: string;
  name: string;
}
interface ProfileImageProps {
  email: string;
  size?: number;
  defaultImage?: string;
  isEditable?: boolean;
}

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
`;

const CustomIcon = styled(TeIcon)<{ $scale: number }>`
  transform-origin: center;
  transform: scale(${({ $scale }) => $scale});
`;

// Cache for gravatar URL resolution results: email -> { url, failed }
const gravatarCache = new Map<string, { url: string; failed: boolean }>();

const ProfileImage: FC<ProfileImageProps> = ({
  email,
  size = 24,
  defaultImage = '404',
  isEditable = false,
}) => {
  const { t } = useTranslation();
  const cacheKey = `${email}-${size}-${defaultImage}`;
  const cached = gravatarCache.get(cacheKey);
  const [profileImageUrl, setProfileImageUrl] = useState<string>(cached?.url ?? '');
  const [imageLoadFailed, setImageLoadFailed] = useState(cached?.failed ?? false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [fileToAdjust, setFileToAdjust] = useState<Logo | null>(null);

  const iconSize = 'xl';
  const iconSizePx = sizeToNumber(iconSize);
  const scale = (size / iconSizePx) * 2; // Adding extra scaling to better fit the container

  const onUploadFile = (file: IUploadFile | null) => {
    setFileToAdjust(file as unknown as Logo);
    setIsModalOpen(false);
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const onProfileImageSubmit = (logo: Blob) => {
    // TODO: implement profile image upload logic
    setFileToAdjust(null);
  };

  // TODO: once profile image upload is implemented, update this to fetch the uploaded image if available
  useEffect(() => {
    // Skip if we already have a cached result
    if (gravatarCache.has(cacheKey)) return;

    let cancelled = false;
    setImageLoadFailed(false);

    getGravatarUrl(email, size, defaultImage).then((url) => {
      if (cancelled) return;

      // Preload via native Image so we can detect failures before handing off to TeImage
      const img = new Image();
      img.onload = () => {
        if (!cancelled) {
          gravatarCache.set(cacheKey, { url, failed: false });
          setProfileImageUrl(url);
        }
      };
      img.onerror = () => {
        if (!cancelled) {
          gravatarCache.set(cacheKey, { url: '', failed: true });
          setImageLoadFailed(true);
          setProfileImageUrl('');
        }
      };
      img.src = url;
    });

    return () => {
      cancelled = true;
    };
  }, [cacheKey, email, size, defaultImage]);

  return (
    <TeContainerSlim relative width={size} height={size}>
      {imageLoadFailed || !profileImageUrl ? (
        <TeContainerSlim width={size} height={size} borderRadius="100%" padding={0}>
          <IconWrapper>
            <CustomIcon name="account_circle" size={iconSize} $scale={scale} />
          </IconWrapper>
        </TeContainerSlim>
      ) : (
        <TeImage
          width={size}
          height={size}
          borderWidth={1}
          borderRadius="100%"
          imageSource={profileImageUrl ?? undefined}
          borderColor="border.secondary"
        />
      )}
      {isEditable && (
        <TeContainerSlim style={{ position: 'absolute', bottom: -10, right: -10 }}>
          <TeButton
            size="m"
            icon="edit"
            rounded
            variant="neutral"
            teclick={() => setIsModalOpen(true)}
          />
        </TeContainerSlim>
      )}
      {isModalOpen && (
        <TeModal
          open={isModalOpen}
          closeMode="button"
          teclose={() => setIsModalOpen(false)}
          modalTitle={t('avatar.modalTitle')}
          contentPadding={0}
          width={'m'}
          closeable
        >
          <TeContainer fill grow padding="l" gap="m" column>
            <UploadFileArea
              acceptedFiles={['image/png', 'image/jpg']}
              maxFileSize={5 * 1024 * 1024}
              onUploadFile={onUploadFile}
            />
          </TeContainer>

          <TeButton
            slot="tertiary"
            text={t('cancel')}
            variant="neutral"
            teclick={() => setIsModalOpen(false)}
            size="s"
          />
          <TeButton
            slot="primary"
            text={t('avatar.submitButton')}
            variant="primary"
            teclick={() => {}}
            disabled={!fileToAdjust}
            size="s"
          />
        </TeModal>
      )}
      {fileToAdjust && (
        <AdjustLogoModal
          title={t('avatar.modalTitle')}
          logo={fileToAdjust}
          onClose={() => setFileToAdjust(null)}
          onSubmit={onProfileImageSubmit}
        />
      )}
    </TeContainerSlim>
  );
};

export default ProfileImage;
